import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignOut, ArrowLeft } from '@phosphor-icons/react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { toast } from 'sonner';

export default function History() {
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await api.get('/sharpslips');
      setSlips(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error(t('loadHistoryError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return 'text-risk-low bg-risk-low/15';
      case 'medium': return 'text-risk-medium bg-risk-medium/15';
      case 'high': return 'text-risk-high bg-risk-high/15';
      default: return 'text-white/70 bg-white/10';
    }
  };

  const getRiskLabel = (risk) => {
    switch (risk) {
      case 'low': return t('low');
      case 'medium': return t('medium');
      case 'high': return t('high');
      default: return risk;
    }
  };

  const getLocale = () => {
    const locales = {
      pt: 'pt-PT',
      en: 'en-US',
      es: 'es-ES',
      fr: 'fr-FR',
      de: 'de-DE'
    };
    return locales[language] || 'en-US';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(getLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background grain-overlay">
      {/* Navbar */}
      <nav className="border-b border-white/5 backdrop-blur-md bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="SharpBetAI" className="h-12 w-auto" />
          </div>
          <div className="flex items-center gap-4">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white transition-colors duration-150"
              data-testid="dashboard-link"
            >
              <ArrowLeft weight="duotone" className="text-lg" />
              <span className="font-medium">{t('dashboard')}</span>
            </Link>
            <LanguageSelector />
            <div className="px-4 py-2 text-white/70 font-medium">{user?.name}</div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white transition-colors duration-150"
              data-testid="logout-button"
            >
              <SignOut weight="duotone" className="text-lg" />
              <span className="font-medium">{t('logout')}</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <h1 className="text-3xl font-heading font-black text-white mb-8">{t('historyTitle')}</h1>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : slips.length === 0 ? (
          <div className="glass-surface p-12 rounded-sm text-center" data-testid="empty-history">
            <div className="text-white/40 text-lg mb-4">{t('noSlipsYet')}</div>
            <Link 
              to="/dashboard" 
              className="inline-block px-8 py-3 bg-primary text-background font-bold rounded-full hover:bg-primary/90 transition-all duration-150"
            >
              {t('generateFirstSlipBtn')}
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {slips.map((slip, idx) => (
              <div key={slip.slip_id} className="glass-surface p-6 rounded-sm" data-testid={`history-slip-${idx}`}>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-white/40 text-sm">{formatDate(slip.created_at)}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${getRiskColor(slip.risk_level)}`}>
                        {getRiskLabel(slip.risk_level)}
                      </span>
                    </div>
                    <div className="text-white/50 text-sm">{t('targetOddsLabel')}: {slip.target_odds}x</div>
                  </div>
                  <div className="text-right">
                    <div className="text-white/40 text-xs uppercase mb-1">{t('finalOdds')}</div>
                    <div className="text-primary font-mono text-3xl font-bold">{slip.combined_odds.toFixed(2)}x</div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/5 p-4 rounded-sm border border-white/10">
                    <div className="text-white/40 text-xs uppercase mb-1">{t('probability')}</div>
                    <div className="text-secondary font-mono text-2xl font-bold">{slip.probability.toFixed(1)}%</div>
                  </div>
                  <div className="bg-white/5 p-4 rounded-sm border border-white/10">
                    <div className="text-white/40 text-xs uppercase mb-1">{t('confidence')}</div>
                    <div className="text-risk-low font-mono text-2xl font-bold">{slip.confidence_score.toFixed(1)}%</div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {slip.selected_matches.map((match, matchIdx) => (
                    <div key={matchIdx} className="bg-white/5 p-3 rounded-sm border border-white/10 flex items-center justify-between">
                      <div>
                        <div className="text-white text-sm font-semibold">{match.home_team} vs {match.away_team}</div>
                        <div className="text-white/40 text-xs">{match.market}: {match.selection}</div>
                      </div>
                      <div className="text-primary font-mono font-bold">{match.odds.toFixed(2)}</div>
                    </div>
                  ))}
                </div>

                <div className="bg-secondary/10 border border-secondary/20 p-4 rounded-sm">
                  <div className="text-white/40 text-xs uppercase mb-2">{t('aiAnalysis')}</div>
                  <p className="text-white/60 text-sm leading-relaxed">{slip.ai_reasoning}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
